import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ID } from '@proofroom/schema';
import { Header } from '../components/Header';
import { AgencyRail, useIsAgency } from '../components/AgencyRail';
import { ComparePanel } from '../components/ComparePanel';
import { MoveDocumentDialog } from '../components/MoveDocumentDialog';
import { ShareDialog } from '../components/ShareDialog';
import { Chip, EmptyState, ErrorState, Spinner, formatDate } from '../components/ui';
import { useDocument } from '../api/queries';
import { useSession } from '../auth/session';
import { documents as documentsApi } from '../api/endpoints';
import { ApiError } from '../api/client';

export function DocumentPage() {
  const { documentId = '' } = useParams();
  const q = useDocument(documentId);
  const doc = q.data;
  const drafts = [...(doc?.drafts ?? [])].sort((a, b) => b.number - a.number);
  const agency = useIsAgency();
  const { user } = useSession();
  const isAdmin = !!user && user.role === 'admin';

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveBusy, setMoveBusy] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const closeMove = useCallback(() => { setMoveOpen(false); setMoveError(null); }, []);
  const handleMove = async (projectId: ID) => {
    setMoveError(null);
    setMoveBusy(true);
    try {
      await documentsApi.move(documentId, projectId);
      // the breadcrumbs read the project off the document, so a refetch is what moves them
      await q.refetch();
      setMoveOpen(false);
    } catch (err) {
      setMoveError(err instanceof ApiError || err instanceof Error ? err.message : 'Could not move the document');
    } finally {
      setMoveBusy(false);
    }
  };
  const [shareOpen, setShareOpen] = useState(false);
  return (
    <div className="app">
      <Header crumbs={[{ label: 'Projects', to: '/projects' }, ...(doc ? [{ label: 'Documents', to: `/projects/${doc.projectId}` }] : []), { label: doc?.name ?? 'Document' }]} />
      <main className="main">
        <div className={agency ? 'page with-rail' : 'page'}>
          {agency && <AgencyRail documentId={documentId} />}
          <div className="page-body">
            <div className="page-head">
              <div><h1>{doc?.name ?? 'Document'}</h1><span className="sub">Drafts published from InDesign, newest first</span></div>
              <div className="btn-row">
                {isAdmin && doc && <button className="btn" onClick={() => setMoveOpen(true)}>Move to project</button>}
                {/* the server gate is agency staff on the document; the same rule the rail uses picks who sees the button */}
                {agency && <button className="btn" onClick={() => setShareOpen(true)}>Share</button>}
                <Link className="btn" to={`/documents/${documentId}/audit`}>Audit log</Link>
                {agency && <Link className="btn" to={`/documents/${documentId}/feedback`}>Export feedback</Link>}
                {doc?.currentDraftId && <Link className="btn primary" to={`/documents/${documentId}/drafts/${doc.currentDraftId}`}>Open current draft</Link>}
              </div>
            </div>
            {q.isPending && <Spinner label="Loading drafts" />}
            {q.isError && <ErrorState title="Could not load the document" error={q.error} retry={() => void q.refetch()} />}
            {doc && drafts.length === 0 && <EmptyState title="No drafts yet">The designer has not published a draft of this document.</EmptyState>}
            {doc && drafts.length > 1 && <ComparePanel documentId={documentId} drafts={drafts} currentDraftId={doc.currentDraftId} />}
            {doc && drafts.length > 0 && (
              <div className="card timeline">
                {drafts.map((d) => {
                  const current = d.id === doc.currentDraftId;
                  return (
                    <div key={d.id} className={`draft${current ? ' current' : ''}`}>
                      <div className="num">{d.number}</div>
                      <div>
                        <div className="title">{d.label ?? `Draft ${d.number}`} {current && <Chip kind="accent">current</Chip>} {d.approvedAt && <Chip kind="ok">Approved</Chip>} {d.prunedAt && <Chip kind="superseded">files pruned</Chip>}</div>
                        <div className="meta">{d.publishedAt ? `Published ${formatDate(d.publishedAt)}` : `Created ${formatDate(d.createdAt)}`} · from {d.source}{d.notes ? ` · ${d.notes}` : ''}</div>
                      </div>
                      {/* a pruned draft has no snapshot left to draw, so it is not a link at all rather than one that lands on an explanation */}
                      {d.prunedAt
                        ? <button className="btn sm" disabled title="This draft's files were pruned to reclaim space">Open</button>
                        : <Link className="btn sm" to={`/documents/${documentId}/drafts/${d.id}`}>Open</Link>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>
      {doc && (
        <MoveDocumentDialog open={moveOpen} documentName={doc.name} currentProjectId={doc.projectId} busy={moveBusy} error={moveError} onMove={(id) => void handleMove(id)} onClose={closeMove} />
      )}
      {agency && <ShareDialog documentId={documentId} open={shareOpen} onClose={() => setShareOpen(false)} />}
    </div>
  );
}
